import React from 'react';

import { type CustomRolePermissionLevel, type PlatformLevelPermissions } from '@column/api';

import { OptionsFull, OptionsNotCreatable, OptionsNotEditable, OptionsOnOff } from '../types';
import { RenderFields } from '~/components';
import { type SectionField } from '~/components/RenderFields';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';

import { PermissionSelect } from './PermissionSelect';

type PermissionKey = keyof KeysCamelCase<PlatformLevelPermissions>;

interface Props {
  excludeKeys?: PermissionKey[];
  fieldPathPrefix?: string;
  includeKeys?: PermissionKey[];
  onOffEnabledPermissionLevel?: `${CustomRolePermissionLevel}`;
}

export const AdditionalPermissions: React.FC<Props> = ({
  excludeKeys = [],
  fieldPathPrefix,
  includeKeys,
  onOffEnabledPermissionLevel = 'write',
}) => {
  const usersPermissionEnabled = useFeatureFlag(FeatureFlag.CustomRoleUsersPermissionEnabled);
  const fieldPath = (field: string) => `${fieldPathPrefix ? `${fieldPathPrefix}.` : ''}${field}`;
  const isHidden = (key: PermissionKey) =>
    excludeKeys.includes(key) || (includeKeys !== undefined && !includeKeys.includes(key));

  const permissionField = (
    key: PermissionKey,
    label: string,
    description: string,
    options: { label: string; value: string }[],
    hide = isHidden(key)
  ): SectionField<any, undefined> => ({
    hide,
    id: fieldPath(`platformLevelPermissions.${key}`),
    label,
    description,
    children: ({ value, onChange }) => (
      <PermissionSelect
        aria-label={label}
        // Roles saved before the Users permission existed leave it unset.
        value={value === 'default' || (key === 'users' && !value) ? 'none' : value}
        options={options}
        onChange={onChange}
      />
    ),
  });

  return RenderFields<any>({
    sections: [
      {
        hide: isHidden('platformInfo') && (!usersPermissionEnabled || isHidden('users')),
        fields: [
          permissionField(
            'platformInfo',
            'Platform Settings',
            'View and edit platform information and due diligence.',
            OptionsNotCreatable
          ),
          permissionField(
            'users',
            'User Management',
            'View and manage users, invites, and role assignments.',
            OptionsFull,
            !usersPermissionEnabled || isHidden('users')
          ),
        ],
      },
      {
        fields: [
          permissionField(
            'counterparties',
            'Counterparties',
            'Set the default permissions for new and existing counterparties.',
            OptionsNotEditable
          ),
          permissionField(
            'apiKeys',
            'API Keys',
            'API keys allow developers on your team to connect to your platform. Access should be granted sparingly.',
            OptionsFull
          ),
          permissionField(
            'transferTemplates',
            'Transfer Templates',
            'Set the default permissions for new and existing transfer templates.',
            OptionsFull
          ),
          permissionField(
            'webhooks',
            'Events and Webhooks',
            'Set the default permissions for events and webhooks.',
            OptionsFull
          ),
          permissionField(
            'loans',
            'Loans',
            'Set the default loan permissions for new and existing bank accounts.',
            OptionsFull
          ),
        ],
      },
      {
        hide: isHidden('reporting'),
        fields: [
          {
            id: fieldPath('platformLevelPermissions.reporting'),
            label: 'Reports and Statements',
            description: 'Set the default reporting permissions for new and existing bank accounts.',
            children: ({ value, onChange }) => (
              <PermissionSelect
                aria-label="Reports and Statements"
                value={value === 'write' || value === 'read' ? 'on' : 'off'}
                options={OptionsOnOff}
                onChange={(newValue) => onChange(newValue === 'on' ? onOffEnabledPermissionLevel : 'none')}
              />
            ),
          },
        ],
      },
    ],
  });
};
